/**
 * Shows the sticky CTA bar after the primary add-to-cart button is out of view.
 * Falls back to viewport threshold if target selector is unavailable.
 */
(function () {
  const DEFAULT_THRESHOLD = 2.5;
  const MIN_THRESHOLD = 0.5;
  const MAX_THRESHOLD = 10;
  const MOBILE_MAX_WIDTH = 767;
  const TABLET_MAX_WIDTH = 1024;

  function parseNumber(value) {
    if (value == null || value === "") return null;
    const normalized = String(value).trim().replace(",", ".");
    const num = parseFloat(normalized, 10);
    if (!Number.isFinite(num)) return null;
    return num;
  }

  function parseThreshold(value) {
    const num = parseNumber(value);
    if (num === null || num < MIN_THRESHOLD || num > MAX_THRESHOLD) return null;
    return num;
  }

  function parseOffset(value, fallback) {
    const parsed = parseNumber(value);
    if (parsed === null) return fallback;
    return Math.max(0, Math.round(parsed));
  }

  function getBreakpointOffset(stickyCta) {
    const desktopOffset = parseOffset(stickyCta.getAttribute("data-sticky-cta-offset-desktop"), 0);
    const tabletOffset = parseOffset(stickyCta.getAttribute("data-sticky-cta-offset-tablet"), desktopOffset);
    const mobileOffset = parseOffset(stickyCta.getAttribute("data-sticky-cta-offset-mobile"), tabletOffset);

    if (window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches) {
      return mobileOffset;
    }

    if (window.matchMedia(`(max-width: ${TABLET_MAX_WIDTH}px)`).matches) {
      return tabletOffset;
    }

    return desktopOffset;
  }

  function isAtPageTop() {
    return (window.scrollY || window.pageYOffset || 0) <= 0;
  }

  function initStickyCta() {
    const stickyCta = document.querySelector("[data-sticky-cta]");
    if (!stickyCta) return;

    const targetSelector = stickyCta.getAttribute("data-sticky-cta-target-selector");
    let observer = null;
    let fallbackScrollHandler = null;
    let fallbackResizeHandler = null;
    let topGuardScrollHandler = null;
    let fallbackThresholdPx = 0;
    let fallbackRafScheduled = false;
    let anchorOutOfView = false;

    function applyStickyInteractionState(isVisible) {
      stickyCta.style.pointerEvents = isVisible ? "auto" : "none";
      stickyCta.setAttribute("aria-hidden", isVisible ? "false" : "true");
    }

    function setStickyVisibility(shouldShow) {
      const isVisible = shouldShow && !isAtPageTop();

      if (isVisible) {
        stickyCta.classList.add("active");
      } else {
        stickyCta.classList.remove("active");
      }

      applyStickyInteractionState(isVisible);
    }

    function updateVisibilityFromAnchor() {
      setStickyVisibility(anchorOutOfView);
    }

    function initTopGuardListener() {
      if (topGuardScrollHandler) return;

      topGuardScrollHandler = function () {
        updateVisibilityFromAnchor();
      };

      window.addEventListener("scroll", topGuardScrollHandler, { passive: true });
    }

    function clearTopGuardListener() {
      if (!topGuardScrollHandler) return;
      window.removeEventListener("scroll", topGuardScrollHandler);
      topGuardScrollHandler = null;
    }

    function initThresholdFallback() {
      const thresholdAttr = stickyCta.getAttribute("data-sticky-cta-threshold");
      const parsedThreshold = parseThreshold(thresholdAttr);
      const thresholdMultiplier = parsedThreshold !== null ? parsedThreshold : DEFAULT_THRESHOLD;

      function updateFromThreshold() {
        anchorOutOfView = window.scrollY >= fallbackThresholdPx;
        updateVisibilityFromAnchor();
      }

      fallbackScrollHandler = function () {
        if (fallbackRafScheduled) return;
        fallbackRafScheduled = true;
        requestAnimationFrame(function () {
          fallbackRafScheduled = false;
          fallbackThresholdPx = thresholdMultiplier * window.innerHeight;
          updateFromThreshold();
        });
      };

      fallbackResizeHandler = fallbackScrollHandler;
      fallbackThresholdPx = thresholdMultiplier * window.innerHeight;
      window.addEventListener("scroll", fallbackScrollHandler, { passive: true });
      window.addEventListener("resize", fallbackResizeHandler);
      initTopGuardListener();
      updateFromThreshold();
    }

    function destroyObserver() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    function createObserver() {
      destroyObserver();
      const targetButton = targetSelector ? document.querySelector(targetSelector) : null;

      if (!targetButton) {
        initThresholdFallback();
        return;
      }

      const offset = getBreakpointOffset(stickyCta);
      observer = new IntersectionObserver(
        function (entries) {
          const entry = entries[0];
          if (!entry) return;
          anchorOutOfView = !entry.isIntersecting;
          updateVisibilityFromAnchor();
        },
        {
          threshold: 0,
          root: null,
          rootMargin: `${offset}px 0px 0px 0px`,
        }
      );

      observer.observe(targetButton);
      initTopGuardListener();
      anchorOutOfView = false;
      updateVisibilityFromAnchor();
    }

    function clearFallbackListeners() {
      if (fallbackScrollHandler) {
        window.removeEventListener("scroll", fallbackScrollHandler);
        fallbackScrollHandler = null;
      }
      if (fallbackResizeHandler) {
        window.removeEventListener("resize", fallbackResizeHandler);
        fallbackResizeHandler = null;
      }
      fallbackThresholdPx = 0;
      fallbackRafScheduled = false;
    }

    function rebuildVisibilityListeners() {
      clearFallbackListeners();
      clearTopGuardListener();
      anchorOutOfView = false;
      createObserver();
    }

    applyStickyInteractionState(false);
    rebuildVisibilityListeners();
    window.addEventListener("resize", rebuildVisibilityListeners);
    window.addEventListener("orientationchange", rebuildVisibilityListeners);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStickyCta);
  } else {
    initStickyCta();
  }
})();
